import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  buildManualBillingCsvFilename,
  BILLING_MISSING_SESSION_PRICE_MESSAGE,
  STUDENT_BILLING_REPORT_DEFAULT_PAGE_SIZE,
  type AttendanceExportCategory,
  type ManualBillingReportCategory,
  type ManualBillingReportRow,
  type StudentBillingReportRow,
  type StudentBillingSessionRow,
  type StudentBillingReportSummary,
} from '@court-sync/shared';
import { academyStudentService } from '@/services/academyStudentService';
import { organizationService } from '@/services/organization';
import { useAttendanceExportScope } from '@/hooks/useAttendanceExportScope';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Switch } from '@/components/ui/Switch';
import type { PaginationState } from '@/components/ui/PaginatedTable';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/Table';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/Alert';
import {
  DashboardDataCard,
  DashboardFilterCard,
} from '@/components/dashboard/DashboardSectionCard';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { downloadBlob, downloadCSV } from '@/utils/csvUtils';
import {
  buildStudentBillingDrilldownCsvRows,
} from '@/utils/attendanceCsvExport';
import { formatCurrency, type Currency } from '@/utils/currency';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/Popover';
import { ChevronRight, Filter, TriangleAlert } from 'lucide-react';
import { Tooltip } from '@/components/ui/Tooltip';
import toast from 'react-hot-toast';

const FILTER_SELECT_CLASS =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:border-primary-500 focus-visible:ring-2 focus-visible:ring-primary-500';

const EMPTY_BILLING_SUMMARY: StudentBillingReportSummary = {
  totalDue: 0,
  sessionsAttended: 0,
  sessionsTotal: 0,
};

const SOURCE_CATEGORY_OPTIONS: { value: ManualBillingReportCategory; label: string }[] = [
  { value: 'all', label: 'All sources' },
  { value: 'lesson', label: 'Lessons' },
  { value: 'play_event', label: 'Play events' },
];

function participantTypeLabel(type?: ManualBillingReportRow['participantType']): string {
  switch (type) {
    case 'member':
      return 'Member';
    case 'guest':
      return 'Guest';
    case 'student':
      return 'Student';
    default:
      return '—';
  }
}

function sourceLabel(source: ManualBillingReportRow['source']): string {
  return source === 'play_event' ? 'Play event' : 'Lesson';
}

function paymentStatusLabel(status: string): string {
  if (status === 'paid') return 'Paid';
  if (status === 'unpaid') return 'Unpaid';
  if (status === 'waived' || status === 'comped') return 'Comped';
  return status;
}

function MobileBillingRowContent({
  name,
  itemName,
  row,
  money,
  rowDue,
  showChevron = false,
}: {
  name: string;
  itemName: string;
  row: ManualBillingReportRow;
  money: (n: number) => string;
  rowDue: (row: ManualBillingReportRow) => number | null;
  showChevron?: boolean;
}) {
  return (
    <>
      <div className="flex items-center justify-between gap-2">
        <span className="font-semibold leading-snug text-foreground">{name}</span>
        {showChevron ? (
          <ChevronRight className="size-4 shrink-0 text-muted-foreground" aria-hidden />
        ) : null}
      </div>
      <p className="text-sm text-muted-foreground">
        {sourceLabel(row.source)} · {participantTypeLabel(row.participantType)} · {itemName}
      </p>
      <dl className="grid grid-cols-2 gap-x-3 gap-y-2 text-sm">
        <div className="space-y-0.5">
          <dt className="text-xs font-medium text-muted-foreground">Date</dt>
          <dd className="font-medium text-foreground">
            {row.source === 'play_event' ? row.eventDate ?? '—' : row.periodStart ?? '—'}
          </dd>
        </div>
        <div className="space-y-0.5">
          <dt className="text-xs font-medium text-muted-foreground">Amount due</dt>
          <dd className="tabular-nums font-semibold text-foreground">
            {rowDue(row) != null ? money(rowDue(row)!) : '—'}
          </dd>
        </div>
        <div className="space-y-0.5">
          <dt className="text-xs font-medium text-muted-foreground">Payment</dt>
          <dd className="font-medium text-foreground">{paymentStatusLabel(row.paymentStatus)}</dd>
        </div>
      </dl>
    </>
  );
}

const LESSON_CATEGORY_OPTIONS: { value: '' | AttendanceExportCategory; label: string }[] = [
  { value: '', label: 'All lesson types' },
  { value: 'term_group', label: 'Term group lessons' },
  { value: 'private_in_term', label: 'Private in term' },
  { value: 'private_standalone', label: 'Standalone private' },
  { value: 'camp', label: 'Camps' },
];

function billingReportBillByAttendanceStorageKey(academyId: string): string {
  return `courtsync.billingReport.billByAttendance.${academyId}`;
}

function readBillByAttendancePreference(academyId: string): boolean {
  try {
    return localStorage.getItem(billingReportBillByAttendanceStorageKey(academyId)) === '1';
  } catch {
    return false;
  }
}

type BillingPeriodMode = 'month' | 'range';

function currentBillableMonth(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

/** `YYYY-MM` → first and last calendar day of that month (`YYYY-MM-DD`). */
function monthToPeriod(ym: string): { periodStart: string; periodEnd: string } | null {
  if (!ym || !/^\d{4}-\d{2}$/.test(ym)) return null;
  const [year, month] = ym.split('-').map(Number);
  if (month < 1 || month > 12) return null;
  const mm = String(month).padStart(2, '0');
  const lastDay = new Date(year, month, 0).getDate();
  return {
    periodStart: `${year}-${mm}-01`,
    periodEnd: `${year}-${mm}-${String(lastDay).padStart(2, '0')}`,
  };
}

function currentMonthDateRange(): { from: string; to: string } {
  const period = monthToPeriod(currentBillableMonth());
  return {
    from: period?.periodStart ?? '',
    to: period?.periodEnd ?? '',
  };
}

function openMonthPicker(input: HTMLInputElement) {
  if (typeof input.showPicker !== 'function') return;
  try {
    input.showPicker();
  } catch {
    // showPicker may throw when not allowed in this browser context.
  }
}

function billingBasisLabel(row: StudentBillingReportRow): string {
  switch (row.billingBasis) {
    case 'attendance_in_period':
      return 'Attendance in period';
    case 'term_full_price_due_in_period':
      return 'Term billed this period';
    case 'term_full_price_outside_period':
      return 'Term billed outside period';
    case 'enrollment_total':
      return 'Enrollment total';
    case 'missing_session_price':
      return '';
    default:
      return '';
  }
}

export type ManualBillingContext = 'academy_only' | 'club_only' | 'combined';

export interface StudentBillingReportProps {
  organizationId: string;
  academyId?: string;
  clubId?: string;
  currency?: Currency;
  billingContext?: ManualBillingContext;
}

export function StudentBillingReport({
  organizationId,
  academyId,
  clubId,
  currency = 'USD',
  billingContext = 'academy_only',
}: StudentBillingReportProps) {
  const exportScope = useAttendanceExportScope(academyId);
  const hasAcademy = Boolean(academyId);
  const showLessonFilters = hasAcademy;
  const [sourceCategory, setSourceCategory] = useState<ManualBillingReportCategory>('all');
  const defaultMonthRange = useMemo(() => currentMonthDateRange(), []);
  const [periodMode, setPeriodMode] = useState<BillingPeriodMode>('month');
  const [billableMonth, setBillableMonth] = useState(currentBillableMonth);
  const [rangeStart, setRangeStart] = useState(defaultMonthRange.from);
  const [rangeEnd, setRangeEnd] = useState(defaultMonthRange.to);
  const period = useMemo(() => {
    if (periodMode === 'month') {
      return monthToPeriod(billableMonth);
    }
    if (!rangeStart || !rangeEnd || rangeStart > rangeEnd) {
      return null;
    }
    return { periodStart: rangeStart, periodEnd: rangeEnd };
  }, [periodMode, billableMonth, rangeStart, rangeEnd]);
  const [loading, setLoading] = useState(false);
  const [exportingCsv, setExportingCsv] = useState(false);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState<PaginationState | null>(null);
  const [summary, setSummary] = useState<StudentBillingReportSummary>(EMPTY_BILLING_SUMMARY);
  const [rows, setRows] = useState<ManualBillingReportRow[]>([]);
  const [selected, setSelected] = useState<{ studentId: string; lessonId: string; label: string } | null>(
    null
  );
  const [drilldown, setDrilldown] = useState<StudentBillingSessionRow[]>([]);
  const [loadingDrill, setLoadingDrill] = useState(false);
  const [billByAttendance, setBillByAttendanceState] = useState(() =>
    academyId ? readBillByAttendancePreference(academyId) : false
  );

  useEffect(() => {
    if (academyId) {
      setBillByAttendanceState(readBillByAttendancePreference(academyId));
    }
  }, [academyId]);

  const setBillByAttendance = useCallback(
    (checked: boolean) => {
      setBillByAttendanceState(checked);
      if (!academyId) return;
      try {
        localStorage.setItem(
          billingReportBillByAttendanceStorageKey(academyId),
          checked ? '1' : '0'
        );
      } catch {
        // localStorage may be unavailable (private browsing, quota, etc.)
      }
    },
    [academyId]
  );

  const effectiveSourceCategory: ManualBillingReportCategory =
    billingContext === 'club_only' ? 'play_event' : sourceCategory;

  const loadKey = useMemo(
    () =>
      JSON.stringify({
        organizationId,
        academyId,
        clubId,
        sourceCategory: effectiveSourceCategory,
        scope: exportScope.scope,
        periodStart: period?.periodStart ?? null,
        periodEnd: period?.periodEnd ?? null,
        billByAttendance,
      }),
    [
      organizationId,
      academyId,
      clubId,
      effectiveSourceCategory,
      exportScope.scope,
      period,
      billByAttendance,
    ]
  );
  const loadKeyForPageRef = useRef(loadKey);
  const requestPage = loadKeyForPageRef.current !== loadKey ? 1 : page;

  useEffect(() => {
    if (loadKeyForPageRef.current !== loadKey) {
      loadKeyForPageRef.current = loadKey;
      setPage(1);
    }
  }, [loadKey]);

  const load = useCallback(async () => {
    if (!period) {
      setRows([]);
      setPagination(null);
      setSummary(EMPTY_BILLING_SUMMARY);
      return;
    }
    setLoading(true);
    try {
      const res = await organizationService.getManualBillingReport(organizationId, {
        academyId,
        clubId,
        category: effectiveSourceCategory,
        termId: exportScope.scope.termId,
        lessonId: exportScope.scope.lessonId,
        lessonCategory: exportScope.scope.category,
        periodStart: period?.periodStart,
        periodEnd: period?.periodEnd,
        billByAttendance: hasAcademy ? billByAttendance : false,
        page: requestPage,
        limit: STUDENT_BILLING_REPORT_DEFAULT_PAGE_SIZE,
      });
      if (!res.success || !res.data) {
        toast.error((res as { error?: string }).error || 'Failed to load billing report');
        setRows([]);
        setPagination(null);
        setSummary(EMPTY_BILLING_SUMMARY);
        return;
      }
      setRows(res.data.aggregates);
      setPagination(res.data.pagination ?? null);
      setSummary(res.data.summary ?? EMPTY_BILLING_SUMMARY);
    } catch (e: unknown) {
      setRows([]);
      setPagination(null);
      setSummary(EMPTY_BILLING_SUMMARY);
      toast.error(e instanceof Error ? e.message : 'Failed to load billing report');
    } finally {
      setLoading(false);
    }
  }, [
    organizationId,
    academyId,
    clubId,
    effectiveSourceCategory,
    exportScope.scope,
    period,
    billByAttendance,
    hasAcademy,
    requestPage,
  ]);

  useEffect(() => {
    void load();
  }, [load]);

  const loadDrill = useCallback(
    async (row: ManualBillingReportRow) => {
      if (row.source !== 'lesson' || !row.studentId || !row.lessonId || !academyId) return;
      setLoadingDrill(true);
      setSelected({
        studentId: row.studentId,
        lessonId: row.lessonId,
        label: `${row.firstName} ${row.lastName} — ${row.lessonName ?? 'Lesson'}`,
      });
      try {
        const res = await academyStudentService.getStudentBillingReport(academyId, {
          ...exportScope.scope,
          studentId: row.studentId,
          lessonId: row.lessonId,
          detailed: true,
          periodStart: period?.periodStart,
          periodEnd: period?.periodEnd,
          billByAttendance,
        });
        setDrilldown(res.data?.detailed ?? []);
      } catch {
        setDrilldown([]);
      } finally {
        setLoadingDrill(false);
      }
    },
    [academyId, exportScope.scope, period, billByAttendance]
  );

  const totals = summary;

  const exportMain = async () => {
    if (!period) {
      toast.error('Choose a valid billing period (end date must be on or after start date).');
      return;
    }
    const filename = buildManualBillingCsvFilename(period.periodStart, period.periodEnd);

    setExportingCsv(true);
    try {
      const blob = await organizationService.exportManualBillingReportCsv(organizationId, {
        academyId,
        clubId,
        category: effectiveSourceCategory,
        termId: exportScope.scope.termId,
        lessonId: exportScope.scope.lessonId,
        lessonCategory: exportScope.scope.category,
        periodStart: period.periodStart,
        periodEnd: period.periodEnd,
        billByAttendance: hasAcademy ? billByAttendance : false,
      });
      downloadBlob(filename, blob);
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Failed to export billing report');
    } finally {
      setExportingCsv(false);
    }
  };

  const handlePeriodModeChange = (mode: BillingPeriodMode) => {
    setPeriodMode(mode);
    if (mode === 'range') {
      const monthPeriod = monthToPeriod(billableMonth);
      if (monthPeriod) {
        setRangeStart(monthPeriod.periodStart);
        setRangeEnd(monthPeriod.periodEnd);
      }
    }
  };

  const exportDrill = () => {
    if (!selected) return;
    downloadCSV(
      `student-billing-sessions_${selected.studentId}_${selected.lessonId}.csv`,
      buildStudentBillingDrilldownCsvRows(drilldown)
    );
  };

  const money = (n: number) => formatCurrency(n, currency);
  const rowDue = (row: ManualBillingReportRow): number | null => {
    if (row.source === 'lesson' && row.billingBasis === 'missing_session_price') return null;
    return row.periodTotalDue ?? row.totalDue;
  };
  const dueCell = (row: ManualBillingReportRow) => {
    const due = rowDue(row);
    if (due != null) {
      return (
        <>
          {money(due)}
          {billingBasisLabel(row as StudentBillingReportRow) ? (
            <span className="block text-xs font-normal text-muted-foreground">
              {billingBasisLabel(row as StudentBillingReportRow)}
            </span>
          ) : null}
        </>
      );
    }
    return (
      <span className="inline-flex items-center justify-end gap-1.5">
        <span className="text-muted-foreground">—</span>
        <Tooltip content={BILLING_MISSING_SESSION_PRICE_MESSAGE} side="left">
          <button
            type="button"
            className="inline-flex rounded-sm text-amber-600 hover:text-amber-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:text-amber-400 dark:hover:text-amber-300"
            aria-label={BILLING_MISSING_SESSION_PRICE_MESSAGE}
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
          >
            <TriangleAlert className="size-4 shrink-0" aria-hidden />
          </button>
        </Tooltip>
      </span>
    );
  };
  const handleRowKeyDown = (event: React.KeyboardEvent, row: ManualBillingReportRow) => {
    if (row.source !== 'lesson') return;
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    void loadDrill(row);
  };

  const hasActiveScopeFilters = Boolean(
    exportScope.category || exportScope.termId || exportScope.lessonId
  );

  const clearScopeFilters = () => {
    exportScope.setCategory('');
    exportScope.setTermId('');
    exportScope.setLessonId('');
  };

  const hasReportRows = (pagination?.total ?? 0) > 0;
  const missingSessionPriceCount = summary.missingSessionPriceCount ?? 0;
  const showMissingSessionPriceWarning =
    billByAttendance && !loading && missingSessionPriceCount > 0;

  const paginationControls =
    pagination && pagination.totalPages > 1 ? (
      <div className="flex flex-col gap-3 border-t border-border px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
        <span className="text-sm text-muted-foreground">
          Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
        </span>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setPage(Math.max(1, pagination.page - 1))}
            disabled={pagination.page <= 1 || loading}
          >
            Previous
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setPage(pagination.page + 1)}
            disabled={pagination.page >= pagination.totalPages || loading}
          >
            Next
          </Button>
        </div>
      </div>
    ) : null;

  const filterDescription =
    billingContext === 'club_only'
      ? 'Play event fees in the selected period. Mark payments on each event roster, then reconcile here.'
      : billByAttendance
        ? 'Due this period uses list per-session or per-day prices multiplied by attended sessions in the selected period.'
        : 'Fixed term and full camp packages are charged when they start in the selected period; session-priced lessons bill attended sessions in that period. Play events use the event date in the selected period.';

  return (
    <div className="space-y-6">
      <DashboardFilterCard
        title="Filters"
        description={filterDescription}
      >
          <div className="flex flex-col gap-3 min-[480px]:flex-row min-[480px]:items-end min-[480px]:justify-between">
            <div className="flex min-w-0 flex-wrap items-end gap-2 sm:gap-3">
              <div className="min-w-0 w-full min-[480px]:w-auto">
                <label
                  htmlFor="billing-report-period-mode"
                  className="mb-1 block text-xs font-medium text-muted-foreground"
                >
                  Billing period
                </label>
                <select
                  id="billing-report-period-mode"
                  aria-label="Billing period type"
                  value={periodMode}
                  onChange={(e) => handlePeriodModeChange(e.target.value as BillingPeriodMode)}
                  className={`${FILTER_SELECT_CLASS} h-9`}
                >
                  <option value="month">Billable month</option>
                  <option value="range">Custom date range</option>
                </select>
              </div>
              {periodMode === 'month' ? (
                <div className="min-w-0 w-full min-[480px]:w-auto">
                  <label
                    htmlFor="billing-report-month"
                    className="mb-1 block text-xs font-medium text-muted-foreground"
                  >
                    Month
                  </label>
                  <Input
                    id="billing-report-month"
                    type="month"
                    aria-label="Billable month"
                    value={billableMonth}
                    onChange={(e) => setBillableMonth(e.target.value)}
                    onClick={(e) => openMonthPicker(e.currentTarget)}
                    className="relative h-9 w-full min-[480px]:w-44 cursor-pointer py-2 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:inset-0 [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-0"
                  />
                </div>
              ) : (
                <>
                  <div className="min-w-0 w-full min-[480px]:w-auto">
                    <label
                      htmlFor="billing-report-from"
                      className="mb-1 block text-xs font-medium text-muted-foreground"
                    >
                      From
                    </label>
                    <Input
                      id="billing-report-from"
                      type="date"
                      aria-label="Billing period start date"
                      value={rangeStart}
                      max={rangeEnd || undefined}
                      onChange={(e) => setRangeStart(e.target.value)}
                      className="h-9 w-full min-[480px]:w-40"
                    />
                  </div>
                  <div className="min-w-0 w-full min-[480px]:w-auto">
                    <label
                      htmlFor="billing-report-to"
                      className="mb-1 block text-xs font-medium text-muted-foreground"
                    >
                      To
                    </label>
                    <Input
                      id="billing-report-to"
                      type="date"
                      aria-label="Billing period end date"
                      value={rangeEnd}
                      min={rangeStart || undefined}
                      onChange={(e) => setRangeEnd(e.target.value)}
                      className="h-9 w-full min-[480px]:w-40"
                    />
                  </div>
                </>
              )}
              {hasAcademy && billingContext !== 'club_only' ? (
                <div className="min-w-0 w-full min-[480px]:w-auto">
                  <label
                    htmlFor="billing-report-source-category"
                    className="mb-1 block text-xs font-medium text-muted-foreground"
                  >
                    Source
                  </label>
                  <select
                    id="billing-report-source-category"
                    aria-label="Billing report source"
                    value={sourceCategory}
                    onChange={(e) =>
                      setSourceCategory(e.target.value as ManualBillingReportCategory)
                    }
                    className={`${FILTER_SELECT_CLASS} h-9`}
                  >
                    {SOURCE_CATEGORY_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}
              {showLessonFilters && effectiveSourceCategory !== 'play_event' ? (
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="inline-flex shrink-0 items-center gap-2"
                  >
                    <Filter className="h-4 w-4" aria-hidden />
                    Filters
                    {hasActiveScopeFilters && (
                      <span className="ml-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-primary-600 px-1.5 text-xs text-white">
                        •
                      </span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className="w-[min(450px,90vw)] max-h-[80vh] overflow-y-auto p-4"
                  align="start"
                >
                  <div className="space-y-4">
                    <div className="flex items-center justify-between gap-2">
                      <h3 className="font-semibold text-gray-900 dark:text-white">
                        Filter report
                      </h3>
                      {hasActiveScopeFilters && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-8 text-muted-foreground"
                          onClick={clearScopeFilters}
                        >
                          Clear
                        </Button>
                      )}
                    </div>
                    <div className="space-y-3">
                      <div className="min-w-0">
                        <label
                          htmlFor="billing-report-category"
                          className="mb-1 block text-xs font-medium text-muted-foreground"
                        >
                          Category
                        </label>
                        <select
                          id="billing-report-category"
                          aria-label="Billing report category"
                          value={exportScope.category}
                          onChange={(e) =>
                            exportScope.setCategory(e.target.value as '' | AttendanceExportCategory)
                          }
                          disabled={exportScope.loading}
                          className={FILTER_SELECT_CLASS}
                        >
                          {LESSON_CATEGORY_OPTIONS.map((o) => (
                            <option key={o.value || 'all'} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="min-w-0">
                        <label
                          htmlFor="billing-report-term"
                          className="mb-1 block text-xs font-medium text-muted-foreground"
                        >
                          Term (optional)
                        </label>
                        <select
                          id="billing-report-term"
                          aria-label="Billing report term"
                          value={exportScope.termId}
                          onChange={(e) => exportScope.setTermId(e.target.value)}
                          disabled={exportScope.loading}
                          className={FILTER_SELECT_CLASS}
                        >
                          <option value="">All terms</option>
                          {exportScope.terms.map((t) => (
                            <option key={t.id} value={t.id}>
                              {t.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="min-w-0">
                        <label
                          htmlFor="billing-report-lesson"
                          className="mb-1 block text-xs font-medium text-muted-foreground"
                        >
                          Lesson (optional)
                        </label>
                        <select
                          id="billing-report-lesson"
                          aria-label="Billing report lesson"
                          value={exportScope.lessonId}
                          onChange={(e) => exportScope.setLessonId(e.target.value)}
                          disabled={exportScope.loading}
                          className={FILTER_SELECT_CLASS}
                        >
                          <option value="">All in scope</option>
                          {exportScope.filteredLessons.map((l) => (
                            <option key={l.id} value={l.id}>
                              {l.name} ({l.lessonType})
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
              ) : null}
              {showLessonFilters && effectiveSourceCategory !== 'play_event' ? (
              <div className="flex items-center gap-2.5 whitespace-nowrap">
                <Switch
                  id="billing-report-bill-by-attendance"
                  checked={billByAttendance}
                  onCheckedChange={setBillByAttendance}
                  aria-label="Bill by attendance"
                />
                <Label
                  htmlFor="billing-report-bill-by-attendance"
                  className="cursor-pointer font-normal text-foreground"
                >
                  Bill by attendance
                </Label>
              </div>
              ) : null}
            </div>
            <Button
              type="button"
              size="sm"
              className="w-full min-[480px]:w-auto"
              onClick={() => void exportMain()}
              disabled={loading || exportingCsv || !hasReportRows}
            >
              {exportingCsv ? 'Exporting…' : 'Export CSV'}
            </Button>
          </div>
      </DashboardFilterCard>

      {showMissingSessionPriceWarning ? (
        <Alert variant="warning">
          <TriangleAlert className="size-4" aria-hidden />
          <AlertTitle>Missing per-session prices</AlertTitle>
          <AlertDescription>
            {missingSessionPriceCount === 1
              ? '1 enrollment cannot be billed by attendance because its lesson has no per-session price.'
              : `${missingSessionPriceCount} enrollments cannot be billed by attendance because their lessons have no per-session price.`}{' '}
            {BILLING_MISSING_SESSION_PRICE_MESSAGE}
            {pagination && pagination.totalPages > 1
              ? ' Some affected rows may be on other pages—check the Due this period column or export CSV.'
              : null}
          </AlertDescription>
        </Alert>
      ) : null}

      <DashboardDataCard
        title="Billing Report"
        description={
          hasAcademy
            ? 'Tap or select a lesson row to view session-level attendance and amounts.'
            : 'Play event fees due for manual collection in the selected period.'
        }
      >
        <div className="md:hidden">
          {loading ? (
            <p className="px-4 py-10 text-center text-sm text-muted-foreground">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="px-4 py-10 text-center text-sm text-muted-foreground">
              No rows match these filters.
            </p>
          ) : (
            <>
              <ul className="divide-y divide-border">
                {rows.map((r) => {
                  const name = `${r.firstName} ${r.lastName}`.trim();
                  const itemName =
                    r.source === 'play_event' ? r.eventName ?? 'Play event' : r.lessonName ?? 'Lesson';
                  const isLesson = r.source === 'lesson';
                  return (
                    <li key={r.rowId}>
                      {isLesson ? (
                        <button
                          type="button"
                          onClick={() => void loadDrill(r)}
                          className="flex w-full flex-col gap-3 px-4 py-4 text-left transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background active:bg-muted/70"
                        >
                          <MobileBillingRowContent
                            name={name}
                            itemName={itemName}
                            row={r}
                            money={money}
                            rowDue={rowDue}
                            showChevron
                          />
                        </button>
                      ) : (
                        <div className="flex w-full flex-col gap-3 px-4 py-4">
                          <MobileBillingRowContent
                            name={name}
                            itemName={itemName}
                            row={r}
                            money={money}
                            rowDue={rowDue}
                          />
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
              <div className="border-t border-border bg-muted/30 px-4 py-4">
                <dl className="grid grid-cols-2 gap-x-3 gap-y-2 text-sm">
                  <div className="space-y-0.5">
                    <dt className="text-xs font-medium text-muted-foreground">Sessions</dt>
                    <dd className="tabular-nums font-semibold text-foreground">
                      {totals.sessionsAttended}/{totals.sessionsTotal}
                    </dd>
                  </div>
                  <div className="space-y-0.5">
                    <dt className="text-xs font-medium text-muted-foreground">Due this period</dt>
                    <dd className="tabular-nums font-semibold text-foreground">
                      {money(totals.totalDue)}
                    </dd>
                  </div>
                </dl>
              </div>
              {paginationControls}
            </>
          )}
        </div>

        <div className="hidden md:block">
          <div className="overflow-x-auto">
            <Table aria-label="Billing report" className="min-w-[960px]">
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead className="h-11 whitespace-nowrap px-4 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Person
                  </TableHead>
                  <TableHead className="h-11 whitespace-nowrap px-4 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Type
                  </TableHead>
                  <TableHead className="h-11 whitespace-nowrap px-4 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Source
                  </TableHead>
                  <TableHead className="h-11 whitespace-nowrap px-4 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Name
                  </TableHead>
                  <TableHead className="h-11 whitespace-nowrap px-4 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Date
                  </TableHead>
                  <TableHead className="h-11 whitespace-nowrap px-4 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Payment
                  </TableHead>
                  <TableHead className="h-11 whitespace-nowrap px-4 text-right text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Amount due
                  </TableHead>
                  <TableHead className="h-11 w-10 px-2" aria-hidden />
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell
                      colSpan={8}
                      className="px-4 py-12 text-center text-sm text-muted-foreground"
                    >
                      Loading…
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={8}
                      className="px-4 py-12 text-center text-sm text-muted-foreground"
                    >
                      No rows match these filters.
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((r) => {
                    const itemName =
                      r.source === 'play_event' ? r.eventName ?? '—' : r.lessonName ?? '—';
                    const rowDate =
                      r.source === 'play_event' ? r.eventDate ?? '—' : r.periodStart ?? '—';
                    const isLesson = r.source === 'lesson';
                    return (
                      <TableRow
                        key={r.rowId}
                        role={isLesson ? 'button' : undefined}
                        tabIndex={isLesson ? 0 : undefined}
                        className={
                          isLesson
                            ? 'cursor-pointer border-border hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background'
                            : 'border-border'
                        }
                        onClick={isLesson ? () => void loadDrill(r) : undefined}
                        onKeyDown={isLesson ? (event) => handleRowKeyDown(event, r) : undefined}
                      >
                        <TableCell className="px-4 py-3 font-medium text-foreground">
                          {r.firstName} {r.lastName}
                        </TableCell>
                        <TableCell className="px-4 py-3 text-muted-foreground">
                          {participantTypeLabel(r.participantType)}
                        </TableCell>
                        <TableCell className="px-4 py-3 text-muted-foreground">
                          {sourceLabel(r.source)}
                        </TableCell>
                        <TableCell className="px-4 py-3 text-foreground">
                          {itemName}
                          {r.source === 'lesson' && r.termName ? (
                            <span className="block text-xs text-muted-foreground">{r.termName}</span>
                          ) : null}
                        </TableCell>
                        <TableCell className="px-4 py-3 text-muted-foreground">{rowDate}</TableCell>
                        <TableCell className="px-4 py-3 text-muted-foreground">
                          {paymentStatusLabel(r.paymentStatus)}
                        </TableCell>
                        <TableCell className="px-4 py-3 text-right tabular-nums font-medium text-foreground">
                          {dueCell(r)}
                        </TableCell>
                        <TableCell className="px-2 py-3">
                          {isLesson ? (
                            <ChevronRight className="size-4 text-muted-foreground" aria-hidden />
                          ) : null}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
              {!loading && rows.length > 0 ? (
                <TableFooter>
                  <TableRow className="border-t-2 border-border bg-muted/40 hover:bg-muted/40">
                    <TableCell className="px-4 py-3 font-semibold text-foreground" colSpan={6}>
                      Totals
                    </TableCell>
                    <TableCell className="px-4 py-3 text-right tabular-nums font-semibold text-foreground">
                      {money(totals.totalDue)}
                    </TableCell>
                    <TableCell className="px-2 py-3" />
                  </TableRow>
                </TableFooter>
              ) : null}
            </Table>
          </div>
          {paginationControls}
        </div>
      </DashboardDataCard>

      <Dialog open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent className="flex max-h-[min(90vh,720px)] w-[calc(100vw-1rem)] max-w-3xl flex-col gap-0 overflow-hidden p-0 sm:w-full">
          <DialogHeader className="shrink-0 space-y-1 border-b border-border px-4 pb-4 pt-2 text-left sm:px-6 sm:pb-4 sm:pt-3">
            <DialogTitle>Session detail</DialogTitle>
            <DialogDescription>{selected?.label}</DialogDescription>
            <div className="pt-3">
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="w-full sm:w-auto"
                onClick={exportDrill}
                disabled={loadingDrill || drilldown.length === 0}
              >
                Export CSV
              </Button>
            </div>
          </DialogHeader>
          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-6">
            {loadingDrill ? (
              <p className="text-sm text-muted-foreground">Loading sessions…</p>
            ) : drilldown.length === 0 ? (
              <p className="text-sm text-muted-foreground">No attendance records yet.</p>
            ) : (
              <ul className="space-y-2 text-sm">
                {drilldown.map((s, i) => (
                  <li
                    key={`${s.sessionDate}-${i}`}
                    className="flex justify-between gap-2 border-b border-border pb-2"
                  >
                    <span>
                      {s.sessionDate} · {s.status}
                    </span>
                    <span className="tabular-nums">{money(s.price)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
